import {
  exportStudioBreakdownCsv,
  exportStudioBreakdownExcel,
  toNum,
  type IFixedBreakdownRow,
  type IStudioBreakdownBlock,
  type IStudioBreakdownExcelConfig,
  type TBreakdownInfoCol,
} from '@sg/utils/studioBreakdownExcel';

export interface IBurnRateBreakdownItem {
  level1: string;
  level2: string;
  level3?: string | null;
  value?: number | null;
}

export interface IBurnRateBreakdownMonthRow {
  date?: string;
  // Temporary: some responses still send `month` until backend migrates to `date`.
  month?: string;
  source?: string;
  is_actual?: number;
  items?: IBurnRateBreakdownItem[];
}

/** Portfolio Overview download: one studio with nested month series. */
export interface IBurnRatePortfolioStudioRow {
  studio_id?: string;
  studio_name: string;
  series?: IBurnRateBreakdownMonthRow[];
}

export interface IExportBurnRateExcelParams {
  datatime: string;
  name?: string;
  fileFormat?: 'excel' | 'csv';
  /** Studios page: flat month rows for one studio. */
  rawData?: IBurnRateBreakdownMonthRow[];
  studioName?: string;
  /** Portfolio Overview: nested studios → series months. */
  studios?: IBurnRatePortfolioStudioRow[];
}

const SUMMARY_BOLD_COLUMNS: TBreakdownInfoCol[] = [2, 3];
const BLUE = 'FF0070C0';

const normalizeLevel = (value?: string | null): string => (
  value?.trim() || ''
);

const getMonthKey = (row: IBurnRateBreakdownMonthRow): string => (
  row.date || row.month || ''
);

const buildItemKey = (item: IBurnRateBreakdownItem): string => (
  `${normalizeLevel(item.level1)}|${normalizeLevel(item.level2)}|${normalizeLevel(item.level3)}`
);

const findItemValue = (
  monthRow: IBurnRateBreakdownMonthRow,
  level1: string,
  level2: string,
  level3: string,
): number | null => {
  const target = {
    level1: normalizeLevel(level1),
    level2: normalizeLevel(level2),
    level3: normalizeLevel(level3),
  };
  const matchedItem = (monthRow.items || []).find((item) => (
    normalizeLevel(item.level1) === target.level1
    && normalizeLevel(item.level2) === target.level2
    && normalizeLevel(item.level3) === target.level3
  ));
  return toNum(matchedItem?.value);
};

/** Summary / highlight styling inferred from row shape — not a fixed metric list. */
const resolveBurnRateRowStyle = (
  level1: string,
  level2: string,
  level3: string,
): Pick<IFixedBreakdownRow<IBurnRateBreakdownMonthRow>, 'boldColumns' | 'columnColors'> => {
  const l1 = normalizeLevel(level1);
  const l2 = normalizeLevel(level2);
  const l3 = normalizeLevel(level3);
  const style: Pick<IFixedBreakdownRow<IBurnRateBreakdownMonthRow>, 'boldColumns' | 'columnColors'> = {};

  if (!l3 && (l1 === l2 || l2.toLowerCase().includes('excl.'))) {
    style.boldColumns = SUMMARY_BOLD_COLUMNS;
  }
  // "Less …" deductions (Channel Cost, IP royalty, profit sharing, …) + Burn Rate Total → blue bold L2.
  const isLessDeduction = l2.toLowerCase().startsWith('less ');
  const isBurnRateTotal = l1 === 'Burn Rate' && l2 === 'Total';
  if (isLessDeduction || isBurnRateTotal) {
    style.columnColors = { 3: BLUE };
    style.boldColumns = isBurnRateTotal ? SUMMARY_BOLD_COLUMNS : [3];
  }
  return style;
};

// Row order + labels come from API items[] (studios may expose different metrics).
const buildBurnRateFixedRowsFromData = (
  rawData: IBurnRateBreakdownMonthRow[],
): IFixedBreakdownRow<IBurnRateBreakdownMonthRow>[] => {
  const seen = new Set<string>();
  const rowDefs: Array<{ level1: string; level2: string; level3: string }> = [];

  [...rawData]
    .sort((a, b) => getMonthKey(a).localeCompare(getMonthKey(b)))
    .forEach((monthRow) => {
      (monthRow.items || []).forEach((item) => {
        const key = buildItemKey(item);
        if (seen.has(key)) return;
        seen.add(key);
        rowDefs.push({
          level1: item.level1 ?? '',
          level2: item.level2 ?? '',
          level3: normalizeLevel(item.level3),
        });
      });
    });

  return rowDefs.map((rowDef) => ({
    ...rowDef,
    ...resolveBurnRateRowStyle(rowDef.level1, rowDef.level2, rowDef.level3),
    getValue: (monthRow) => findItemValue(
      monthRow,
      rowDef.level1,
      rowDef.level2,
      rowDef.level3,
    ),
  }));
};

const buildStudioBlocks = (
  params: IExportBurnRateExcelParams,
): IStudioBreakdownBlock<IBurnRateBreakdownMonthRow>[] => {
  // Portfolio Overview: nested studios[].series[]; each studio keeps its own row taxonomy.
  if (params.studios?.length) {
    return params.studios.map((studio) => {
      const rawData = studio.series ?? [];
      return {
        studioName: studio.studio_name || '',
        rawData,
        fixedRows: buildBurnRateFixedRowsFromData(rawData),
      };
    });
  }

  const rawData = params.rawData ?? [];
  return [{
    studioName: params.studioName ?? '',
    rawData,
    fixedRows: buildBurnRateFixedRowsFromData(rawData),
  }];
};

const BURN_RATE_EXPORT_BASE_NAME = 'Studio Burn Rate Tracking';

const buildBurnRateBreakdownConfig = (
  params: IExportBurnRateExcelParams,
): IStudioBreakdownExcelConfig<IBurnRateBreakdownMonthRow> => {
  // File name: "Studio Burn Rate Tracking" or "… - Miniclip" on Studios page.
  const name = params.name
    ?? (params.studioName
      ? `${BURN_RATE_EXPORT_BASE_NAME} - ${params.studioName}`
      : BURN_RATE_EXPORT_BASE_NAME);

  return {
    name,
    sheetName: 'Burn Rate',
    noteRows: [
      {
        text: 'Burn rate 剔除: channel cost, IP royalty cost, profit sharing incentive, VGTR (tax credits)',
        bold: true,
      },
      {
        text: 'Outsourcing Cost作为Level 3 Cost item已经被包含在Development cost recognized in COPS, 不需要重复计入Burn Rate的计算中, 仅用作下载分析',
        bold: true,
        color: BLUE,
      },
    ],
    unitLabel: '单位: US $mn',
    infoHeaders: [
      'Studios',
      'Level 1 Cost Item',
      'Level 2 Cost Items',
      'Level 3 Cost Item',
    ],
    datatime: params.datatime,
    getMonthKey,
    // 1 = Actual, 0 = Forecast; omit/null → blank cell (do not default to Forecast).
    isActualMonth: (row) => {
      if (row.source === undefined || row.source === null) return null;
      return row.source === 'actual';
      // if (row.is_actual === undefined || row.is_actual === null) return null;
      // return row.is_actual === 1;
    },
    studioBlocks: buildStudioBlocks(params),
    infoWidths: [20, 22, 36, 34],
  };
};

export const exportBurnRateExcel = async (
  params: IExportBurnRateExcelParams,
): Promise<void> => {
  const config = buildBurnRateBreakdownConfig(params);
  if (params.fileFormat === 'csv') {
    exportStudioBreakdownCsv(config);
    return;
  }
  await exportStudioBreakdownExcel(config);
};
